import type { ResultadoEscrita } from './envelope'

/**
 * Erros das Custom Actions.
 *
 * Existem DOIS canais distintos, e confundi-los foi fonte de bug no projeto de
 * referência:
 *
 *  - **Transporte**: HTTP != 2xx. A plataforma devolve a mensagem do `THROW` do
 *    T-SQL em formatos variados (às vezes duplo-codificada).
 *  - **Negócio**: HTTP **200** com `{ success: 0, errorCode, message }`. Nas
 *    actions públicas deste projeto é o ÚNICO canal de recusa, porque `THROW`
 *    sob `XACT_ABORT` desfaz o lote — e levaria junto o contador de tentativa
 *    do código de acesso, o rate limit e a trilha.
 */
export class ErroAction extends Error {
  readonly status: number
  readonly codigo: string | null
  readonly codigoPrecad: number | null

  constructor(mensagem: string, opcoes: { status?: number; codigo?: string | null } = {}) {
    super(mensagem)
    this.name = 'ErroAction'
    this.status = opcoes.status ?? 0
    this.codigo = opcoes.codigo ?? null
    /* O campo estruturado vem PRIMEIRO. As recusas em HTTP 200 trazem o código
       em `errorCode` e a mensagem em linguagem de gente, sem prefixo nenhum —
       olhar só a mensagem devolvia null justamente no canal que este projeto
       usa para recusar, e o desvio por código nunca disparava. A mensagem
       continua como segunda tentativa porque no canal de THROW (actions
       internas) é lá que o código aparece. */
    this.codigoPrecad = extrairCodigoPrecad(opcoes.codigo) ?? extrairCodigoPrecad(mensagem)
  }
}

/**
 * Extrai o código numérico do prefixo `PRECAD-50xxx` da mensagem.
 *
 * Necessário porque em `THROW` a plataforma devolve só TEXTO — não há campo
 * estruturado. O projeto de referência resolvia isso com `includes()` de
 * substring, que quebra ao menor ajuste de redação. Um código no prefixo é
 * estável; a redação pode mudar à vontade.
 */
export function extrairCodigoPrecad(texto: string | null | undefined): number | null {
  if (!texto) return null
  const achado = /PRECAD-(\d{5})/.exec(texto)
  return achado ? Number(achado[1]) : null
}

/**
 * Melhor mensagem possível a partir do corpo de uma resposta não-2xx.
 * Origem: `extractErrorMessage` (80-100) do ControleRecibosCid.
 */
export function extrairMensagemErro(textoBruto: string | null | undefined): string | null {
  const texto = (textoBruto ?? '').trim()
  if (!texto) return null

  try {
    let no: unknown = JSON.parse(texto)
    // Até 5 desembrulhos: a plataforma duplo-codifica com frequência.
    for (let i = 0; i < 5 && typeof no === 'string'; i++) {
      const interno = no.trim()
      if (!interno || (interno[0] !== '{' && interno[0] !== '[')) break
      no = JSON.parse(interno)
    }
    if (typeof no === 'string' && no.trim()) return no.trim().slice(0, 500)
    if (no && typeof no === 'object') {
      const obj = no as Record<string, unknown>
      const msg = obj.message ?? obj.Message ?? obj.errorMessage ?? obj.error ?? obj.mensagem
      if (typeof msg === 'string' && msg.trim()) return msg.trim().slice(0, 500)
    }
  } catch {
    // Corpo não-JSON e curto: usa cru.
    if (texto.length <= 300) return texto
  }
  return null
}

/**
 * Valida o retorno de uma escrita e devolve o identificador gerado.
 * Origem: `assertWrite` (284-293) do ControleRecibosCid.
 *
 * Lança `ErroAction` quando `success` é `false`/`0` — o erro de negócio que
 * chega em HTTP 200.
 */
export function exigirEscrita(linhas: ResultadoEscrita[]): { entityId: string; warning?: string } {
  const linha = linhas[0]
  if (!linha) throw new ErroAction('Falha ao executar a operação (resposta vazia).', { status: 200 })

  if (linha.success === false || linha.success === 0) {
    throw new ErroAction(linha.message || linha.errorCode || 'Falha ao executar a operação.', {
      status: 200,
      codigo: linha.errorCode ?? null,
    })
  }

  return {
    entityId: linha.entityId != null ? String(linha.entityId) : '',
    ...(linha.warning ? { warning: linha.warning } : {}),
  }
}
